import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { tiaAPI, exportAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Globe, ExternalLink, Trash2, MoreHorizontal, FileDown, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

export const TIATable = ({ assessments, onRefresh }) => {
  const navigate = useNavigate();
  const { isComplianceReadOnly } = useAuth();

  if (!assessments.length) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Globe className="h-4 w-4 text-blue-500" />
          Transfer Impact Assessments
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Organisation</TableHead>
              <TableHead>Destination Country</TableHead>
              <TableHead>Residual Risk</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assessments.map(tia => (
              <TableRow
                key={tia.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/risk/tia/${tia.id}`)}
              >
                <TableCell className="font-mono text-sm">{tia.tia_reference || '-'}</TableCell>
                {/* Spec §TIA table — Organisation source: entity.name */}
                <TableCell>{tia.entity_name || tia.organisation || '-'}</TableCell>
                <TableCell>{tia.destination_country || '-'}</TableCell>
                <TableCell>
                  {tia.residual_risk ? (
                    <Badge variant="outline" className={cn(
                      'capitalize',
                      tia.residual_risk === 'Low'      && 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
                      tia.residual_risk === 'Medium'   && 'bg-amber-500/10   text-amber-400   border-amber-500/20',
                      tia.residual_risk === 'High'     && 'bg-rose-500/10    text-rose-400    border-rose-500/20',
                      tia.residual_risk === 'Very High' && 'bg-red-600/20     text-red-400     border-red-500/30',
                    )}>{tia.residual_risk}</Badge>
                  ) : <span className="text-slate-500 text-sm">—</span>}
                </TableCell>
                <TableCell>
                  {tia.decision ? (
                    <Badge variant="outline" className={cn(
                      tia.decision === 'Approve'                 && 'bg-emerald-500/10 text-emerald-400',
                      tia.decision === 'Approve with Conditions' && 'bg-amber-500/10   text-amber-400',
                      tia.decision === 'Suspend'                 && 'bg-orange-500/10  text-orange-400',
                      tia.decision === 'Reject'                  && 'bg-rose-500/10    text-rose-400',
                    )}>{tia.decision}</Badge>
                  ) : <span className="text-slate-500 text-sm">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">{tia.status?.replace('_', ' ')}</Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* Spec §TIA table — Actions: Open, Export DOCX, Export PDF, Delete */}
                      <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/risk/tia/${tia.id}`); }}>
                        <ExternalLink className="h-4 w-4 mr-2" />Open
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={async e => {
                        e.stopPropagation();
                        try {
                          const res = await exportAPI.tiaDocx(tia.id);
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a'); a.href = url;
                          a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'TIA_Report.docx';
                          document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                          toast.success('DOCX exported');
                        } catch { toast.error('Export failed'); }
                      }} data-testid={`export-tia-docx-${tia.id}`}>
                        <FileDown className="h-4 w-4 mr-2" />Export DOCX
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        // Spec requires Export PDF. No backend PDF generator exists today.
                        toast.info('PDF export is coming soon — please use Export DOCX for now.');
                      }} data-testid={`export-tia-pdf-${tia.id}`}>
                        <FileText className="h-4 w-4 mr-2" />Export PDF
                      </DropdownMenuItem>
                      {!isComplianceReadOnly && (
                        <DropdownMenuItem
                          onClick={async e => {
                            e.stopPropagation();
                            if (window.confirm('Delete this TIA?')) {
                              try { await tiaAPI.delete(tia.id); toast.success('TIA deleted'); onRefresh(); }
                              catch { toast.error('Failed to delete'); }
                            }
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
